import { Stack, alpha, styled, useMediaQuery, useTheme } from "@mui/material";

import {
  BrandLogo,
  NavActions,
  NavSearchButton,
  type HomeNavbarProps,
} from "@/components/menu/HomeNavbar";
import { PAGE_PADDING, SLATE_APPBAR_HEIGHT, gutterFor } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import { useAppSelector } from "@/store";
import OrderTypeMenu from "./OrderTypeMenu";
import StoreRating from "./StoreRating";

/**
 * `nd` — `SlateHomeNavbar`, the navbar for `MODERN_SLATE` and
 * `MODERN_SLATE_VIBRANT` (selected by `isSlateDesign`, i.e.
 * `web_design_type.includes("SLATE")`).
 *
 * Two things make it a different component rather than a variant of `tE`:
 * it is **100px** tall (`l.VT`) instead of 60, and its contents sit inside a
 * floating 40px-radius pill (`np`) with a primary-tinted 135° gradient in
 * light mode. The logo is absolutely centred at 90px (60 below `md`), so both
 * chip groups flow around it.
 */

/** `nc` — the sticky shell. */
const Bar = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== "hidden" &&
    prop !== "isModernDesign" &&
    prop !== "isVibrantDesign",
})<{
  hidden?: boolean;
  isModernDesign?: boolean;
  isVibrantDesign?: boolean;
}>(({ theme, hidden, isModernDesign, isVibrantDesign }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    position: "sticky",
    zIndex: Z_INDEX.NAVBAR,
    top: hidden ? -SLATE_APPBAR_HEIGHT : 0,
    left: 0,
    right: 0,
    height: SLATE_APPBAR_HEIGHT,
    minHeight: SLATE_APPBAR_HEIGHT,
    maxHeight: SLATE_APPBAR_HEIGHT,
    padding: PAGE_PADDING,
    backgroundColor: theme.palette.background.default,
    transition: "top 0.6s ease-in-out",
    // ⚠ `nc` has no `lg` or `xl` step — it jumps straight from the 15px
    // padding to `50px <gutter>` at `md`, and the VIBRANT pair keeps
    // auto-hiding there (`top: r ? -l.VT : 0`).
    [theme.breakpoints.up("md")]: {
      padding: `50px ${gutter.lg}`,
      top: isVibrantDesign ? (hidden ? -SLATE_APPBAR_HEIGHT : 0) : 0,
    },
    [theme.breakpoints.up("xxl")]: { padding: `50px ${gutter.xxl}` },
    [theme.breakpoints.up("xxxl")]: { padding: `50px ${gutter.xxxl}` },
  };
});

/** `np` — the floating pill. */
const Pill = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isLargerThanMobile",
})<{ isLargerThanMobile?: boolean }>(({ theme, isLargerThanMobile }) => ({
  padding: isLargerThanMobile ? "10px 20px" : "5px 10px",
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(
          theme.palette.primary.main,
          0.4,
        )} 100%)`
      : undefined,
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.shadows[2],
  borderRadius: 40,
  overflow: "visible",
}));

export default function SlateHomeNavbar({
  showFirstOptionOnly,
  scrollDirection = "up",
  isModernDesign,
  isVibrantDesign,
}: HomeNavbarProps) {
  const theme = useTheme();
  const isLargerThanMobile = useMediaQuery(theme.breakpoints.up("md"));
  const logoUrl = useAppSelector((state) => state.settings.data.image_url);

  return (
    <Bar
      className={scrollDirection === "up" ? "main-header up" : "main-header"}
      hidden={scrollDirection === "down"}
      isModernDesign={isModernDesign}
      isVibrantDesign={isVibrantDesign}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Pill
        flex={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        position="relative"
        isLargerThanMobile={isLargerThanMobile}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {/* Absolutely centred in BOTH bands — vertically too from `md` up. */}
          <BrandLogo
            logoUrl={logoUrl}
            sx={
              isLargerThanMobile
                ? {
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    alignItems: "center",
                    height: 90,
                  }
                : {
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    height: 90,
                    [theme.breakpoints.down("md")]: { height: 60 },
                  }
            }
          />
          <Stack alignItems="flex-start"><OrderTypeMenu /><StoreRating /></Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {!isLargerThanMobile && <NavSearchButton />}
          <NavActions showFirstOptionOnly={showFirstOptionOnly} />
        </Stack>
      </Pill>
    </Bar>
  );
}
